import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { S, Icon, DataCard, formatCHF } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const StatementView = ({ brokerId, period, isAdmin, onClose }) => {
  const { t } = useTranslation();
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [payDate, setPayDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [payRef, setPayRef] = useState('');
  // D-26: 'all' | 'paid' | 'unpaid'. Applied client-side against
  // statement.rows in the render body.
  const [payStatusFilter, setPayStatusFilter] = useState('all');
  // D-25: per-row selection. Set of policy ids checked in the table.
  // 'Pay Selected' collects the record ids from those rows and hits
  // pay-batch with just those; falls back to allRecordIds() (pay all)
  // when nothing is selected — preserves the prior default.
  const [selectedPolicyIds, setSelectedPolicyIds] = useState(() => new Set());

  const fetchStatement = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(
        `${BASE_URL}/commissions/statement/${brokerId}?period=${encodeURIComponent(period)}`,
        { withCredentials: true }
      );
      setStatement(resp.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [brokerId, period]);

  useEffect(() => { fetchStatement(); }, [fetchStatement]);

  const flash = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const allRecordIds = () => {
    if (!statement) return [];
    const ids = [];
    for (const row of statement.rows) {
      for (const r of row.records) ids.push(r._id);
    }
    return ids;
  };

  const approveAll = async () => {
    if (!statement || !isAdmin) return;
    setBusy(true);
    try {
      const ids = allRecordIds();
      await Promise.all(
        ids.map(id =>
          axios.patch(`${BASE_URL}/commissions/records/${id}`, { status: 'approved' }, { withCredentials: true })
        )
      );
      flash(t('screens.commissions.statement.approvedToast'));
      fetchStatement();
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async () => {
    if (!statement || !isAdmin) return;
    setBusy(true);
    try {
      // D-25: honour per-row selection when the tester picked specific
      // rows; otherwise pay everything (prior behaviour). Collects
      // record ids across selected policy rows.
      let ids;
      if (selectedPolicyIds.size > 0) {
        ids = [];
        for (const row of statement.rows) {
          if (selectedPolicyIds.has(String(row.policyId))) {
            for (const r of row.records) ids.push(r._id);
          }
        }
      } else {
        ids = allRecordIds();
      }
      await axios.post(
        `${BASE_URL}/commissions/pay-batch`,
        { recordIds: ids, paymentDate: payDate, paymentReference: payRef },
        { withCredentials: true }
      );
      setShowPay(false);
      setSelectedPolicyIds(new Set());
      flash(t('screens.commissions.statement.paidToast'));
      fetchStatement();
    } catch (err) {
      setError(err?.response?.data?.message || t('screens.commissions.statement.payFailed'));
    } finally {
      setBusy(false);
    }
  };

  const toggleRow = (policyId) => {
    const key = String(policyId);
    setSelectedPolicyIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const toggleAllRows = () => {
    setSelectedPolicyIds(prev => {
      // If any selected, clear. Otherwise select all visible rows.
      if (prev.size > 0) return new Set();
      return new Set((statement?.rows || []).map(r => String(r.policyId)));
    });
  };

  const exportCsv = () => {
    if (!statement) return;
    const headers = [
      t('screens.commissions.statement.colClient'),
      t('screens.commissions.statement.colPolicy'),
      t('screens.commissions.statement.colProduct'),
      t('screens.commissions.statement.colPremium'),
      t('screens.commissions.statement.colRate'),
      t('screens.commissions.statement.colRecurring'),
      t('screens.commissions.statement.colAcqMultiplier'),
      t('screens.commissions.statement.colAcquisition'),
      t('screens.commissions.statement.colTotal'),
    ];
    const lines = [headers.join(',')];
    for (const row of statement.rows) {
      lines.push([
        JSON.stringify(row.clientName || ''),
        JSON.stringify(row.policyNumber || ''),
        row.product || '',
        row.premiumBasis || 0,
        (row.commissionRate || 0) + '%',
        row.recurringAmount || 0,
        (row.acquisitionMultiplier || 1) + 'x',
        row.acquisitionAmount || 0,
        row.totalAmount || 0,
      ].join(','));
    }
    const brokerName = (statement.broker?.name || 'broker').replace(/[^a-z0-9]/gi, '_');
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commission_statement_${brokerName}_${period}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportPdf = () => window.print();

  if (loading) {
    return (
      <div style={{ padding: 60, display: 'flex', justifyContent: 'center' }}>
        <div
          style={{
            width: 32,
            height: 32,
            border: '3px solid #e2e8f0',
            borderTopColor: S.burgundy,
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!statement || !statement.broker) {
    return (
      <DataCard>
        <div style={{ padding: 40, textAlign: 'center', color: S.textMuted }}>
          {t('screens.commissions.statement.empty')}
        </div>
      </DataCard>
    );
  }

  const { broker, totals, rows: allRows } = statement;

  // D-26: rows now filterable by paid/unpaid. Backend row has a
  // `paidAt` (or `paymentDate`) once paid; anything without is
  // outstanding. Client-side filter keeps this local to the current
  // statement page — no new endpoint contract needed. Filter state
  // lives in the component so it resets on statement reload.
  const isRowPaid = (row) =>
    Array.isArray(row.records) && row.records.length > 0 &&
    row.records.every(r => !!(r.paidAt || r.paymentDate || r.status === 'paid'));
  const rows = allRows.filter(r => {
    if (payStatusFilter === 'all') return true;
    if (payStatusFilter === 'paid') return isRowPaid(r);
    return !isRowPaid(r);
  });

  return (
    <div>
      {toast && (
        <div
          style={{
            marginBottom: 16,
            padding: 10,
            borderRadius: 8,
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            color: '#065f46',
            fontSize: 13,
          }}
        >
          {toast}
        </div>
      )}
      {error && (
        <div
          style={{
            marginBottom: 16,
            padding: 10,
            borderRadius: 8,
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <button
            onClick={onClose}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              background: 'transparent',
              color: S.textMuted,
              border: 'none',
              fontSize: 12,
              cursor: 'pointer',
              marginBottom: 6,
            }}
          >
            <Icon name='arrow-left' color={S.textMuted} size={14} />
            {t('screens.commissions.statement.backToOverview')}
          </button>
          <div style={{ fontSize: 20, fontWeight: 700, color: S.textPrimary }}>
            {t('screens.commissions.statement.title')}
          </div>
          <div style={{ fontSize: 14, color: S.textSecondary, marginTop: 2 }}>
            {broker.name}
            <span style={{ color: S.textMuted, margin: '0 6px' }}>·</span>
            {t('screens.commissions.statement.periodLabel')}: <strong>{period}</strong>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* D-26 paid/unpaid filter chips */}
          <div style={{ display: 'inline-flex', border: `1px solid ${S.borderLight}`, borderRadius: 8, overflow: 'hidden' }}>
            {[
              { key: 'all',    label: t('screens.commissions.statement.filterAll',    { defaultValue: 'All' }) },
              { key: 'unpaid', label: t('screens.commissions.statement.filterUnpaid', { defaultValue: 'Unpaid' }) },
              { key: 'paid',   label: t('screens.commissions.statement.filterPaid',   { defaultValue: 'Paid' }) },
            ].map((c, idx, arr) => {
              const active = payStatusFilter === c.key;
              return (
                <button
                  key={c.key}
                  type='button'
                  onClick={() => setPayStatusFilter(c.key)}
                  style={{
                    padding: '9px 12px',
                    background: active ? S.burgundy : '#fff',
                    color: active ? '#fff' : S.textSecondary,
                    border: 'none',
                    borderRight: idx < arr.length - 1 ? `1px solid ${S.borderLight}` : 'none',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          {isAdmin && (
            <button
              onClick={approveAll}
              disabled={busy || rows.length === 0}
              style={{
                padding: '9px 14px',
                background: '#fff',
                color: S.textPrimary,
                border: `1px solid ${S.borderLight}`,
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: busy ? 'wait' : 'pointer',
              }}
            >
              {t('screens.commissions.statement.approveAll')}
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setShowPay(true)}
              disabled={rows.length === 0}
              style={{
                padding: '9px 14px',
                background: S.burgundy,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {selectedPolicyIds.size > 0
                ? t('screens.commissions.statement.paySelected', {
                    defaultValue: 'Pay Selected ({{count}})',
                    count: selectedPolicyIds.size,
                  })
                : t('screens.commissions.statement.markPaid')}
            </button>
          )}
          <button
            onClick={exportPdf}
            style={{
              padding: '9px 14px',
              background: '#fff',
              color: S.textPrimary,
              border: `1px solid ${S.borderLight}`,
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('screens.commissions.statement.exportPdf')}
          </button>
          <button
            onClick={exportCsv}
            style={{
              padding: '9px 14px',
              background: '#fff',
              color: S.textPrimary,
              border: `1px solid ${S.borderLight}`,
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('screens.commissions.statement.exportCsv')}
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <DataCard>
          <div style={{ padding: 40, textAlign: 'center', color: S.textMuted }}>
            {t('screens.commissions.statement.empty')}
          </div>
        </DataCard>
      ) : (
        <DataCard>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {isAdmin && (
                  <th style={{ padding: '12px 8px', width: 32, borderBottom: `2px solid ${S.borderLight}` }}>
                    {/* D-25: header checkbox toggles all/none */}
                    <input
                      type='checkbox'
                      onChange={toggleAllRows}
                      checked={selectedPolicyIds.size > 0 && selectedPolicyIds.size === rows.length}
                      ref={el => { if (el) el.indeterminate = selectedPolicyIds.size > 0 && selectedPolicyIds.size < rows.length; }}
                    />
                  </th>
                )}
                {[
                  'colClient', 'colPolicy', 'colProduct', 'colPremium',
                  'colRate', 'colRecurring', 'colAcqMultiplier', 'colAcquisition', 'colTotal',
                ].map(k => (
                  <th
                    key={k}
                    style={{
                      padding: '12px 14px',
                      textAlign: 'left',
                      fontSize: 11,
                      fontWeight: 700,
                      color: S.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      borderBottom: `2px solid ${S.borderLight}`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t(`screens.commissions.statement.${k}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.policyId || i} style={{ borderBottom: `1px solid ${S.borderLight}`, background: selectedPolicyIds.has(String(r.policyId)) ? 'rgba(128,0,32,0.05)' : (i % 2 === 0 ? '#fff' : S.bgCard) }}>
                  {isAdmin && (
                    <td style={{ padding: '13px 8px', width: 32 }}>
                      <input
                        type='checkbox'
                        checked={selectedPolicyIds.has(String(r.policyId))}
                        onChange={() => toggleRow(r.policyId)}
                      />
                    </td>
                  )}
                  <td style={{ padding: '13px 14px', fontSize: 13, fontWeight: 600, color: S.textPrimary }}>{r.clientName}</td>
                  <td style={{ padding: '13px 14px', fontSize: 13, color: S.textSecondary }}>{r.policyNumber || '—'}</td>
                  <td style={{ padding: '13px 14px', fontSize: 13, color: S.textPrimary }}>{r.product}</td>
                  <td style={{ padding: '13px 14px', fontSize: 13, color: S.textPrimary }}>{formatCHF(r.premiumBasis)}</td>
                  <td style={{ padding: '13px 14px', fontSize: 13, color: S.burgundy, fontWeight: 600 }}>{r.commissionRate}%</td>
                  <td style={{ padding: '13px 14px', fontSize: 13, color: S.textPrimary, fontWeight: 600 }}>{formatCHF(r.recurringAmount)}</td>
                  <td style={{ padding: '13px 14px', fontSize: 13, color: S.textSecondary }}>{r.acquisitionMultiplier}×</td>
                  <td style={{ padding: '13px 14px', fontSize: 13, color: S.burgundy, fontWeight: 600 }}>{formatCHF(r.acquisitionAmount)}</td>
                  <td style={{ padding: '13px 14px', fontSize: 13, color: S.textPrimary, fontWeight: 700 }}>{formatCHF(r.totalAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataCard>
      )}

      <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
        {[
          { label: 'totalsRecurring', value: totals.recurring, color: S.textPrimary },
          { label: 'totalsAcquisition', value: totals.acquisition, color: S.burgundy },
          { label: 'totalsGrand', value: totals.total, color: S.textPrimary, bold: true },
          { label: 'totalsPaid', value: totals.paid, color: '#10b981' },
          { label: 'totalsOutstanding', value: totals.outstanding, color: totals.outstanding > 0 ? '#f59e0b' : S.textMuted },
        ].map(({ label, value, color, bold }) => (
          <div
            key={label}
            style={{
              padding: 16,
              background: '#fff',
              borderRadius: 12,
              border: `1px solid ${S.borderLight}`,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              {t(`screens.commissions.statement.${label}`)}
            </div>
            <div style={{ fontSize: bold ? 20 : 18, fontWeight: 700, color, marginTop: 4 }}>{formatCHF(value)}</div>
          </div>
        ))}
      </div>

      {showPay && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.35)',
            zIndex: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 420,
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, color: S.textPrimary }}>
              {t('screens.commissions.statement.payDialogTitle')}
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase' }}>
                {t('screens.commissions.statement.payDate')}
              </span>
              <input
                type='date'
                value={payDate}
                onChange={e => setPayDate(e.target.value)}
                style={{
                  padding: '9px 12px',
                  background: '#fff',
                  border: `1px solid ${S.borderLight}`,
                  borderRadius: 8,
                  fontSize: 13,
                  color: S.textPrimary,
                  outline: 'none',
                }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase' }}>
                {t('screens.commissions.statement.payReference')}
              </span>
              <input
                value={payRef}
                onChange={e => setPayRef(e.target.value)}
                style={{
                  padding: '9px 12px',
                  background: '#fff',
                  border: `1px solid ${S.borderLight}`,
                  borderRadius: 8,
                  fontSize: 13,
                  color: S.textPrimary,
                  outline: 'none',
                }}
              />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <button
                onClick={() => setShowPay(false)}
                style={{
                  padding: '9px 14px',
                  background: '#fff',
                  color: S.textSecondary,
                  border: `1px solid ${S.borderLight}`,
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {t('screens.commissions.statement.payCancel')}
              </button>
              <button
                onClick={markPaid}
                disabled={busy}
                style={{
                  padding: '9px 14px',
                  background: S.burgundy,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: busy ? 'wait' : 'pointer',
                }}
              >
                {busy ? '…' : t('screens.commissions.statement.payConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatementView;
